import {
  getAdminV1StoresStoreIdAuctions,
  getAdminV1StoresStoreIdAuctionsId,
  getAdminV1StoresStoreIdAuctionsIdBids,
  getAdminV1StoresStoreIdAuctionsIdOffers,
  getAdminV1StoresStoreIdAuctionsRegistrations,
  getAdminV1StoresStoreIdProductsIdPricing,
  patchAdminV1StoresStoreIdAuctionsId,
  postAdminV1StoresStoreIdAuctions,
  postAdminV1StoresStoreIdAuctionsIdBidsBidIdRetract,
  postAdminV1StoresStoreIdAuctionsIdCancel,
  postAdminV1StoresStoreIdAuctionsIdEnd,
  postAdminV1StoresStoreIdAuctionsIdOffersOfferIdWithdraw,
  postAdminV1StoresStoreIdAuctionsIdSecondChance,
  postAdminV1StoresStoreIdAuctionsRegistrationsIdBlock,
  postAdminV1StoresStoreIdAuctionsRegistrationsIdUnblock,
  type AdminAuction,
  type AdminAuctionOffer,
  type AdminAuctionRegistration,
  type GetAdminV1StoresStoreIdAuctions200,
  type GetAdminV1StoresStoreIdAuctionsIdBids200,
  type GetAdminV1StoresStoreIdAuctionsIdBidsParams,
  type GetAdminV1StoresStoreIdAuctionsParams,
  type GetAdminV1StoresStoreIdAuctionsRegistrations200,
  type GetAdminV1StoresStoreIdAuctionsRegistrationsParams,
  type PatchAdminV1StoresStoreIdAuctionsIdBody,
  type PostAdminV1StoresStoreIdAuctionsBody,
  type StoreVariantPricing,
} from '@nucleus-commerce/api-client/admin';

export type AuctionListParams = GetAdminV1StoresStoreIdAuctionsParams;
export type AuctionBidListParams = GetAdminV1StoresStoreIdAuctionsIdBidsParams;
export type RegistrationListParams = GetAdminV1StoresStoreIdAuctionsRegistrationsParams;

/**
 * Auctions of one store, their bid history and the store's registered bidders. Prices are in the
 * store currency whatever the market. The lot's variants are read from the store product's
 * pricing, the same rows the store catalog shows.
 */
export const auctionsApi = {
  list: (storeId: string, params: AuctionListParams): Promise<GetAdminV1StoresStoreIdAuctions200> =>
    getAdminV1StoresStoreIdAuctions({ storeId }, params),
  get: (storeId: string, id: string): Promise<AdminAuction> =>
    getAdminV1StoresStoreIdAuctionsId({ storeId, id }),
  create: (storeId: string, body: PostAdminV1StoresStoreIdAuctionsBody): Promise<AdminAuction> =>
    postAdminV1StoresStoreIdAuctions({ storeId }, body),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdAuctionsIdBody,
  ): Promise<AdminAuction> => patchAdminV1StoresStoreIdAuctionsId({ storeId, id }, body),
  cancel: (storeId: string, id: string, reason: string): Promise<AdminAuction> =>
    postAdminV1StoresStoreIdAuctionsIdCancel({ storeId, id }, { reason }),
  end: (storeId: string, id: string): Promise<AdminAuction> =>
    postAdminV1StoresStoreIdAuctionsIdEnd({ storeId, id }),
  bids: (
    storeId: string,
    id: string,
    params: AuctionBidListParams,
  ): Promise<GetAdminV1StoresStoreIdAuctionsIdBids200> =>
    getAdminV1StoresStoreIdAuctionsIdBids({ storeId, id }, params),
  /** Answers the auction with the leader and price worked out again. */
  retractBid: (storeId: string, id: string, bidId: string, reason: string): Promise<AdminAuction> =>
    postAdminV1StoresStoreIdAuctionsIdBidsBidIdRetract({ storeId, id, bidId }, { reason }),
  registrations: (
    storeId: string,
    params: RegistrationListParams,
  ): Promise<GetAdminV1StoresStoreIdAuctionsRegistrations200> =>
    getAdminV1StoresStoreIdAuctionsRegistrations({ storeId }, params),
  block: (storeId: string, id: string, reason: string): Promise<AdminAuctionRegistration> =>
    postAdminV1StoresStoreIdAuctionsRegistrationsIdBlock({ storeId, id }, { reason }),
  unblock: (storeId: string, id: string): Promise<AdminAuctionRegistration> =>
    postAdminV1StoresStoreIdAuctionsRegistrationsIdUnblock({ storeId, id }),
  /** Cancels an unpaid winner's order and offers the lot to the next bidder; answers the auction. */
  secondChance: (storeId: string, id: string, expiresInHours: number): Promise<AdminAuction> =>
    postAdminV1StoresStoreIdAuctionsIdSecondChance({ storeId, id }, { expiresInHours }),
  offers: (storeId: string, id: string): Promise<AdminAuctionOffer[]> =>
    getAdminV1StoresStoreIdAuctionsIdOffers({ storeId, id }).then((r) => r.items),
  withdrawOffer: (storeId: string, id: string, offerId: string): Promise<AdminAuction> =>
    postAdminV1StoresStoreIdAuctionsIdOffersOfferIdWithdraw({ storeId, id, offerId }),
  lotVariants: (storeId: string, storeProductId: string): Promise<StoreVariantPricing[]> =>
    getAdminV1StoresStoreIdProductsIdPricing({ storeId, id: storeProductId }).then((r) => r.items),
};
